import React, { useState } from 'react';
import { Lock, User, Moon, Sun, Loader2, BarChart3, MessageSquare, Users2 } from 'lucide-react';
import { loginRequest } from '../lib/auth';

const FEATURES = [
  { icon: BarChart3, label: 'Campaign analytics' },
  { icon: MessageSquare, label: 'Conversations' },
  { icon: Users2, label: 'Leads' },
];

export default function LoginPage({ theme, onToggleTheme, onLogin, authConfigured = true }) {
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const handleSubmit = async (event) => {
    event.preventDefault();
    setError('');
    setLoading(true);
    try {
      const data = await loginRequest(username.trim(), password);
      onLogin(data.token, data.user);
    } catch (err) {
      const msg = err.response?.data?.detail
        || err.response?.data?.error
        || err.message
        || 'Could not sign in';
      setError(typeof msg === 'string' ? msg : 'Could not sign in');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="login-page executive-app">
      <div className="clara-bg-layer" aria-hidden="true">
        <div className="clara-bg-bloom clara-bg-bloom--lavender" />
        <div className="clara-bg-bloom clara-bg-bloom--royal" />
      </div>

      <button
        type="button"
        className="login-theme-toggle"
        onClick={onToggleTheme}
        aria-label={theme === 'light' ? 'Switch to dark theme' : 'Switch to light theme'}
      >
        {theme === 'light' ? <Moon size={18} /> : <Sun size={18} />}
      </button>

      <div className="login-shell">
        <div className="login-panels">
          <div className="login-visual">
            <div className="login-visual-orb login-visual-orb--a" aria-hidden="true" />
            <div className="login-visual-orb login-visual-orb--b" aria-hidden="true" />

            <svg
              className="login-visual-arch"
              viewBox="0 0 420 420"
              fill="none"
              aria-hidden="true"
            >
              <g stroke="currentColor" strokeWidth="1">
                <line x1="30" y1="420" x2="30" y2="150" />
                <line x1="66" y1="420" x2="66" y2="90" />
                <line x1="102" y1="420" x2="102" y2="180" />
                <line x1="138" y1="420" x2="138" y2="60" />
                <line x1="174" y1="420" x2="174" y2="200" />
                <path d="M30 150 Q48 92 66 90 Q84 92 102 180" />
                <path d="M102 180 Q120 108 138 60" />
              </g>
              <g stroke="currentColor" strokeWidth="1" opacity="0.7">
                <circle cx="338" cy="96" r="78" />
                <circle cx="338" cy="96" r="52" />
                <circle cx="338" cy="96" r="26" />
              </g>
            </svg>

            <div className="login-visual-mark-wrap">
              <img
                src={`${process.env.PUBLIC_URL}/favicon.svg`}
                alt=""
                className="login-visual-mark"
              />
            </div>

            <div className="login-visual-body">
              <span className="login-visual-rule" aria-hidden="true" />
              <h2>Run your WhatsApp growth engine from one screen.</h2>
              <p>Monitor campaigns, chat with leads, and track delivery in real time.</p>

              <ul className="login-visual-features">
                {FEATURES.map(({ icon: Icon, label }) => (
                  <li key={label}>
                    <Icon size={16} />
                    {label}
                  </li>
                ))}
              </ul>
            </div>

            <p className="login-visual-foot">Haique Bot · Secure dashboard access</p>
          </div>

          <div className="login-card">
            <div className="login-brand">
              <h1>Welcome back</h1>
              <p>Sign in to continue to your dashboard</p>
            </div>

            <form className="login-form" onSubmit={handleSubmit}>
              <label className="login-field">
                <span>Email or username</span>
                <div className="login-input-wrap">
                  <User size={18} />
                  <input
                    type="text"
                    autoComplete="username"
                    data-testid="login-username"
                    placeholder="you@company.com"
                    value={username}
                    onChange={(e) => setUsername(e.target.value)}
                    required
                  />
                </div>
              </label>

              <label className="login-field">
                <span>Password</span>
                <div className="login-input-wrap">
                  <Lock size={18} />
                  <input
                    type="password"
                    autoComplete="current-password"
                    placeholder="Enter password"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    required
                  />
                </div>
              </label>

              {error ? <div className="login-error" role="alert">{error}</div> : null}

              {!authConfigured && !error ? (
                <div className="login-error login-error--warn" role="status">
                  Server login is not configured yet. Add DASHBOARD_PASSWORD on the backend.
                </div>
              ) : null}

              <button type="submit" className="login-submit" disabled={loading}>
                {loading ? <Loader2 size={18} className="login-spinner" /> : null}
                {loading ? 'Signing in…' : 'Sign in'}
              </button>
            </form>
          </div>
        </div>
      </div>
    </div>
  );
}
